import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import GroupLanding from "./GroupLanding";
import InstructorDetails from "./InstructorDetails";
import { toast } from "react-toastify";
import axios from "axios";
import config from "./config";
import MemberMenu from "./MemberMenu";
import NotificationBell from "./NotificationBell";
import PageLoader, { Spinner } from "./PageLoader";
import { readTheme, writeTheme } from "./communityTheme";
import ProfileModal from "./ProfileModal";
import ChangePasswordModal from "./ChangePasswordModal";
import AccountSettingsModal from "./AccountSettingsModal";
import AuthModal from "./AuthModal";
import applyCommunityFont, { applyCommunityDirection } from "./applyFont";
import CommunityFeed from "./CommunityFeed";
import GroupSwitcher from "./GroupSwitcher";
import InstructorAvatar from "./InstructorAvatar";
import { MembersView, LeaderboardView, RewardsView, EventsView } from "./CommunityViews";
import PolicyFooter from "./PolicyFooter";
import BannerPopup from "./BannerPopup";
import WelcomeFlowers from "./WelcomeFlowers";
import ClosedGroupErrorModal from "./ClosedGroupErrorModal";

const DEFAULT_BANNER =
  "https://img.flexifunnels.com/flexiviral-assets/logo-empty.png";

// Professional default cover for group cards that have no banner of their own.
const GROUP_DEFAULT_BANNER =
  "https://images.unsplash.com/photo-1573164713988-8665fc963095?auto=format&fit=crop&w=1200&q=80";

// The backend hands back these "empty" placeholders when a group has no real
// banner — treat them as missing so the GROUP_DEFAULT_BANNER above is used.
const isEmptyBanner = (url) =>
  !url || /\/(signin-empty|logo-empty)\.png(\?|$)/i.test(url);
const groupBanner = (url) => (isEmptyBanner(url) ? GROUP_DEFAULT_BANNER : url);

const EMPTY_FORM = { name: "", email: "", phone: "", password: "", otp: "", password_confirmation: "", gdpr_consent: false };

// Paid groups (access_type 3) send the buyer here to complete the purchase.
const PAID_CHECKOUT_URL = "https://app.flexifunnel.com";

// "$12" for whole amounts, "$12.50" otherwise.
const formatPrice = (amt) => {
  const n = Number(amt) || 0;
  return `$${n % 1 === 0 ? n : n.toFixed(2)}`;
};

// Bundled fallback favicon used when a community hasn't uploaded its own.
// Lives in fe-flexicommunity/public/.
const DEFAULT_FAVICON = `${process.env.PUBLIC_URL || ""}/favicon.ico`;

// Point the browser tab icon at the given URL, creating the <link> if needed.
function setFavicon(href) {
  if (!href) return;
  let link = document.getElementById("app-favicon");
  if (!link) {
    link = document.createElement("link");
    link.id = "app-favicon";
    link.rel = "icon";
    document.head.appendChild(link);
  }
  link.href = href;
}

const TAB_ICON = {
  groups: (
    <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 5h6v6H4V5zm10 0h6v6h-6V5zM4 15h6v4H4v-4zm10 0h6v4h-6v-4z" />
    </svg>
  ),
  feed: (
    <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.7">
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12l8.954-8.955a1.5 1.5 0 012.122 0L22.5 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75" />
    </svg>
  ),
  members: (
    <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.7">
      <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
    </svg>
  ),
  leaderboard: (
    <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 21h8M12 17v4M7 4h10v5a5 5 0 01-10 0V4zM7 5H4v2a3 3 0 003 3M17 5h3v2a3 3 0 01-3 3" />
    </svg>
  ),
  rewards: (
    <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
      <path strokeLinecap="round" strokeLinejoin="round" d="M20 12v9H4v-9M2 7h20v5H2V7zm10 0v14M12 7S9 7 8 5.5 9 3 10 3.5 12 7 12 7zm0 0s3 0 4-1.5S15 3 14 3.5 12 7 12 7z" />
    </svg>
  ),
  events: (
    <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3M4 11h16M5 5h14a1 1 0 011 1v13a1 1 0 01-1 1H5a1 1 0 01-1-1V6a1 1 0 011-1z" />
    </svg>
  ),
  courses: (
    <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.7">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
    </svg>
  ),
  map: (
    <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
    </svg>
  ),
};

// How many group cards to show per page in the community group list.
const GROUPS_PER_PAGE = 12;

const NAV_TABS = [
  { key: "groups", label: "Community", icon: TAB_ICON.groups },
  { key: "feed", label: "Feed", icon: TAB_ICON.feed },
  { key: "courses", label: "Courses", icon: TAB_ICON.courses },
  { key: "events", label: "Events", icon: TAB_ICON.events },
  { key: "members", label: "Members", icon: TAB_ICON.members },
  { key: "map", label: "Map", icon: TAB_ICON.map },
  { key: "leaderboard", label: "Leaderboard", icon: TAB_ICON.leaderboard },
  { key: "rewards", label: "Rewards", icon: TAB_ICON.rewards },
];

// Studio template (sidebar layout) — community-level sections shown in the left
// rail. These reuse the same in-page `view` state Classic uses; "groups" is the
// default browse/library view.
const STUDIO_NAV = [
  { key: "groups", label: "Browse", icon: TAB_ICON.groups },
  { key: "members", label: "Members", icon: TAB_ICON.members },
  { key: "leaderboard", label: "Leaderboard", icon: TAB_ICON.leaderboard },
  { key: "rewards", label: "Rewards", icon: TAB_ICON.rewards },
  { key: "events", label: "Events", icon: TAB_ICON.events },
];
const STUDIO_TITLES = {
  groups: "Browse groups",
  members: "Members",
  leaderboard: "Leaderboard",
  rewards: "Rewards",
  events: "Events",
};

// Member-only group pages reachable via /:token?gid={group_token}&page={key}.
// "landing" (or no page) is the public group landing page; these require a
// signed-in member, so guests hitting them are redirected to the landing page.
const GROUP_PAGES = ["feed", "courses", "events", "members", "map", "leaderboard", "rewards", "about", "pendingpost"];

const memberKey = (token) => `cm_${token}`;
function readMember(token) {
  try {
    return JSON.parse(localStorage.getItem(memberKey(token)) || "null");
  } catch {
    return null;
  }
}


/**
 * Community group collection page.
 *
 * /:token -> loads the community banner + all groups. A signed-in member can
 * click any group to join it (community_group_members). Auth (Join / Sign in /
 * Forgot password) lives in one interlinked modal.
 */
function GroupCollection() {
  const { token } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const gid = searchParams.get("gid"); // ?gid={group_token} -> a specific group
  const page = searchParams.get("page"); // feed | members | leaderboard | rewards | landing
  const refCode = searchParams.get("ref"); // ?ref={referrer url_token} -> referral link
  const focusPost = searchParams.get("post"); // ?post={post_id} -> deep-link to one post

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [community, setCommunity] = useState(null);
  // Falls back to the FlexiCommunity brand logo if the community's own logo
  // is missing or fails to load. Reset whenever the logo url changes.
  const [communityLogoFailed, setCommunityLogoFailed] = useState(false);
  useEffect(() => {
    setCommunityLogoFailed(false);
  }, [community?.logo]);
  const [groups, setGroups] = useState([]);
  const [totalMembers, setTotalMembers] = useState(0);

  // Signed-in community member (persisted per community).
  const [member, setMember] = useState(() => readMember(token));
  const memberId = member?.community_member_id;

  const [showProfile, setShowProfile] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showAccountSettings, setShowAccountSettings] = useState(false);

  // Group-join UI state
  const [groupBusyId, setGroupBusyId] = useState(null);
  const [pendingGroup, setPendingGroup] = useState(null);
  // Why the auth modal was opened from a group: "join" auto-joins after auth,
  // "login" just enters the group feed (an existing member signing in must not
  // get a "You've joined" toast).
  const [pendingGroupAction, setPendingGroupAction] = useState("join");
  const [notice, setNotice] = useState("");

  // Group-join confirmation modal (opens when a non-joined group card is clicked)
  const [joinGroup, setJoinGroup] = useState(null);
  const [joinMsg, setJoinMsg] = useState("");
  // Closed-group rejection popup (member not on the uploaded allow-list).
  const [closedError, setClosedError] = useState("");

  // The open group feed is driven by the URL (?gid + ?page) — no separate
  // route and no local state, so the address bar is shareable and Back works.
  const inGroupPage = !!gid && GROUP_PAGES.includes(page);

  // Instructor details page — shown via in-page state (no URL param) so the
  // address bar stays clean (no ?iid=…). Holds the instructor id, or null.
  const [instructorView, setInstructorView] = useState(null);
  // Branding accent of the template the instructor link was clicked from, so the
  // instructor page matches that template's colour (falls back to community primary).
  const [instructorAccent, setInstructorAccent] = useState("");
  const [instructorDark, setInstructorDark] = useState(false);

  // In-page tab: "groups" | "members" | "leaderboard" | "rewards"
  const [view, setView] = useState("groups");

  // Mobile: full banner description shown in a bottom sheet.
  const [showBannerDesc, setShowBannerDesc] = useState(false);

  // Group list search
  const [groupQuery, setGroupQuery] = useState("");

  // Group list pagination — show GROUPS_PER_PAGE cards per page.
  const [groupPage, setGroupPage] = useState(1);

  // Discover filter popover: Type (visibility) is chosen first, then Price.
  // "all" means that facet is not constraining the list.
  const [filterOpen, setFilterOpen] = useState(false);
  const [typeFilter, setTypeFilter] = useState("all"); // all | public | private
  const [priceFilter, setPriceFilter] = useState("all"); // all | free | paid | free_trial

  // Auth modal: null | "join" | "signin" | "forgot"
  const [modal, setModal] = useState(null);
  // One-shot welcome flower shower shown right after a successful signup.
  const [welcome, setWelcome] = useState({ show: false, name: "" });
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState({ type: "", text: "" }); // type: "error" | "success"
  // Forgot-password OTP flow: step 1 email → 2 code → 3 new password.
  const [forgotStep, setForgotStep] = useState(1);
  const [resetToken, setResetToken] = useState("");

  const primary = community?.primary_color || readTheme(token) || "#0274F7";
  const myGroups = groups.filter((g) => g.joined);
  const discoverGroups = groups.filter((g) => !g.joined);
  const orderedGroups = member ? [...myGroups, ...discoverGroups] : groups;
  const q = groupQuery.trim().toLowerCase();
  // access_type 2 = private; anything else (1) is public.
  const matchType = (g) =>
    typeFilter === "all" ||
    (typeFilter === "private" ? g.access_type === 2 : g.access_type !== 2);
  const matchPrice = (g) =>
    priceFilter === "all" || (g.pricing_type || "free") === priceFilter;
  const matchSearch = (g) =>
    !q ||
    (g.name || "").toLowerCase().includes(q) ||
    (g.description || "").toLowerCase().includes(q);
  const visibleGroups = orderedGroups.filter(
    (g) => matchSearch(g) && matchType(g) && matchPrice(g)
  );
  const activeFilters = (typeFilter !== "all" ? 1 : 0) + (priceFilter !== "all" ? 1 : 0);

  // Pagination — slice the filtered list into pages of GROUPS_PER_PAGE.
  const totalGroupPages = Math.max(1, Math.ceil(visibleGroups.length / GROUPS_PER_PAGE));
  // Keep the page in range when the list shrinks (search / filter changes).
  const safeGroupPage = Math.min(groupPage, totalGroupPages);
  const pagedGroups = visibleGroups.slice(
    (safeGroupPage - 1) * GROUPS_PER_PAGE,
    safeGroupPage * GROUPS_PER_PAGE
  );
  // Reset to the first page whenever the search query or filters change.
  useEffect(() => {
    setGroupPage(1);
  }, [groupQuery, typeFilter, priceFilter, view]);
  const subtitle = community
    ? stripHtml(community.description) || stripHtml(community.headline)
    : "";

  const saveMember = (m) => {
    setMember(m);
    try {
      localStorage.setItem(memberKey(token), JSON.stringify(m));
    } catch {
      /* ignore */
    }
  };
  const signOut = () => {
    setMember(null);
    try {
      localStorage.removeItem(memberKey(token));
    } catch {
      /* ignore */
    }
    setGroups((gs) => gs.map((g) => ({ ...g, joined: false })));
  };

  const loadGroups = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(`${config.COMMUNITY_API_URL}/community-groups`, {
        community_token: token,
        community_member_id: memberId || undefined,
      });
      if (res.data?.error === 0) {
        setCommunity(res.data.community);
        writeTheme(token, res.data.community?.primary_color);
        applyCommunityFont(res.data.community?.membership_font);
        applyCommunityDirection(res.data.community?.rtl);
        setGroups(res.data.groups || []);
        setTotalMembers(res.data.total_members || 0);
      } else {
        setError(res.data?.msg || "Unable to load this community.");
      }
    } catch (err) {
      setError(err.response?.data?.msg || "Something went wrong while loading the community.");
    } finally {
      setLoading(false);
    }
  }, [token, memberId]);

  useEffect(() => {
    if (token) loadGroups();
  }, [token, loadGroups]);

  // Reload the group list when returning from a group page to the collection,
  // so joined/left changes — and a sign-out done inside the feed — are reflected
  // with the now-settled member state (no stale reload racing the logout).
  const wasInGroupPage = useRef(inGroupPage);
  useEffect(() => {
    if (wasInGroupPage.current && !inGroupPage) loadGroups();
    wasInGroupPage.current = inGroupPage;
  }, [inGroupPage, loadGroups]);

  // A shared group link (?gid&page=feed) opened by someone who isn't logged in
  // should NOT drop them into the read-only feed — send them to that group's
  // landing page (banner + Join / Sign In) so they can join first. Once they
  // authenticate, `member` is set and the pendingGroup effect takes them into
  // the feed normally, so this never bounces a real member.
  useEffect(() => {
    if (inGroupPage && !member) {
      setSearchParams(refCode ? { gid, ref: refCode } : { gid }, { replace: true });
    }
  }, [inGroupPage, member, gid, refCode, setSearchParams]);

  // Tab favicon: the community's uploaded favicon if it has one, else the
  // bundled FlexiLogo default.
  useEffect(() => {
    setFavicon(community?.favicon || DEFAULT_FAVICON);
  }, [community?.favicon]);

  // Tab title: the community name (falls back to FlexiCommunity) — without it
  // the browser shows the raw URL (e.g. "localhost:3000") in the tab.
  useEffect(() => {
    document.title = community?.name ? community.name : "FlexiCommunity";
  }, [community?.name]);

  // Keep the avatar/profile button in sync with the saved profile photo.
  useEffect(() => {
    if (!memberId) return;
    let active = true;
    axios
      .post(`${config.COMMUNITY_API_URL}/community-member-profile`, {
        community_token: token,
        community_member_id: memberId,
      })
      .then((res) => {
        if (!active || res.data?.error !== 0) return;
        const fresh = res.data.member;
        if (fresh && (fresh.img !== member?.img || fresh.name !== member?.name)) {
          saveMember({ ...member, name: fresh.name, email: fresh.email, img: fresh.img });
        }
      })
      .catch(() => {});
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, memberId]);

  const openModal = (name) => {
    setMsg({ type: "", text: "" });
    setForm(EMPTY_FORM);
    setForgotStep(1);
    setResetToken("");
    setModal(name);
  };
  const closeModal = () => !busy && setModal(null);

  // ---- Join a specific group (insert into community_group_members) ----
  const doGroupJoin = useCallback(
    async (g) => {
      setGroupBusyId(g.group_id);
      setNotice("");
      try {
        const res = await axios.post(`${config.COMMUNITY_API_URL}/community-group-join`, {
          community_token: token,
          community_member_id: memberId,
          member_token: member?.auth_token,
          group_token: g.group_token,
        });
        if (res.data?.error === 0) {
          setGroups((prev) =>
            prev.map((x) =>
              x.group_id === g.group_id
                ? {
                    ...x,
                    joined: !!res.data.joined,
                    pending: !!res.data.pending,
                    member_count: res.data.member_count ?? x.member_count,
                  }
                : x
            )
          );
          if (res.data.pending) {
            const m = res.data.msg || "Request sent for admin approval.";
            setNotice(m);
            toast.info(m);
          } else if (res.data.joined) {
            toast.success(`You've joined ${g.name}! 🎉`);
          }
        } else if (res.data?.closed_group) {
          // Not on the closed group's allow-list → popup only, no toast/notice
          // and (by virtue of the error path) no welcome animation.
          setClosedError(res.data?.msg || "");
        } else {
          const m = res.data?.msg || "Could not join the group.";
          setNotice(m);
          toast.error(m);
        }
        return res.data;
      } catch (err) {
        if (err.response?.data?.closed_group) {
          setClosedError(err.response.data.msg || "");
          return err.response.data;
        }
        const m = err.response?.data?.msg || "Could not join the group.";
        setNotice(m);
        toast.error(m);
        return { error: 1, msg: m };
      } finally {
        setGroupBusyId(null);
      }
    },
    [token, memberId, member?.auth_token]
  );

  const openFeed = (g) => setSearchParams({ gid: g.group_token, page: "feed" });

  // Clicking a group card opens that group's public landing page in a NEW TAB
  // (instead of the inline Sign In / join-confirmation popup). The landing page
  // itself handles Join / Log In and entering the feed.
  const onGroupClick = (g) => {
    if (!g?.group_token) return;
    const url = `${window.location.origin}/${token}?gid=${g.group_token}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  // Confirm join from the group-join modal: a free group opens its feed right
  // away; a private group sends an approval request and shows the result inline.
  const confirmGroupJoin = async () => {
    const g = joinGroup;
    if (!g) return;
    if (!member) {
      // Not signed in yet — ask them to join/sign in, then auto-join this group.
      setJoinGroup(null);
      setPendingGroup(g);
      setPendingGroupAction("join");
      openModal("join");
      return;
    }
    setJoinMsg("");
    const data = await doGroupJoin(g);
    if (data?.error === 0 && data.joined) {
      setJoinGroup(null);
      openFeed(g);
    } else if (data?.error === 0 && data.pending) {
      setJoinMsg(data.msg || "Request sent for admin approval.");
    } else if (data?.closed_group) {
      // doGroupJoin already opened the closed-group popup — close this modal.
      setJoinGroup(null);
    } else {
      setJoinMsg(data?.msg || "Could not join the group.");
    }
  };

  // After auth completes (modal closed + member set), auto-join the pending
  // group and open its feed (free groups) so the member lands inside directly.
  // openFeed replaces the URL with ?gid&page=feed, so "Back" from the feed
  // returns to the community page rather than the landing page.
  useEffect(() => {
    if (member && pendingGroup && !modal) {
      const g = pendingGroup;
      const action = pendingGroupAction;
      setPendingGroup(null);
      // Login intent: the member just signed in — take them into the group feed
      // without auto-joining (no "You've joined" toast).
      if (action === "login") {
        setSearchParams({ gid: g.group_token, page: "feed" });
        return;
      }
      // Paid groups require payment after login — keep the member on the landing
      // page (where "Get access" now opens checkout), don't auto-join/enter.
      if ((g.pricing_type || "free") === "paid") return;
      doGroupJoin(g).then((data) => {
        if (data?.error === 0 && data.joined) {
          setSearchParams({ gid: g.group_token, page: "feed" });
        }
      });
    }
  }, [member, pendingGroup, pendingGroupAction, modal, doGroupJoin, setSearchParams]);

  // Group pages (feed/members/leaderboard/rewards) are browseable by guests:
  // the content renders read-only and any write action (post/comment/like) or
  // restricted header action opens the Sign In popup inside CommunityFeed.
  // Private groups are still protected server-side (community-feed returns an
  // access error that CommunityFeed surfaces), so no client-side gate here.

  const submitJoin = async (e) => {
    e.preventDefault();
    // GDPR: block signup if consent is mandatory and not given.
    if (community?.gdpr?.enabled && community?.gdpr?.mandatory && !form.gdpr_consent) {
      return setMsg({ type: "error", text: "Please accept the Privacy and Terms to continue." });
    }
    // Phone: block signup if the community made the phone number mandatory.
    if (Number(community?.phone_required) === 1 && !String(form.phone || "").replace(/\D/g, "")) {
      return setMsg({ type: "error", text: "Please enter your phone number to continue." });
    }
    setBusy(true);
    setMsg({ type: "", text: "" });
    try {
      const res = await axios.post(`${config.COMMUNITY_API_URL}/community-join`, {
        community_token: token,
        // Pass the group being joined so the backend can enforce a Closed Group's
        // email allow-list at registration time (block non-allowed signups).
        group_token: pendingGroup?.group_token,
        ref: new URLSearchParams(window.location.search).get("ref") || undefined,
        ...form,
      });
      if (res.data?.error === 0) {
        saveMember(res.data.member);
        setTotalMembers((n) => n + 1);
        setModal(null);
        setForm(EMPTY_FORM);
        // New customer onboarding: celebrate with a flower shower. After a
        // guest signup the pendingGroup effect redirects into the group feed
        // (a fresh CommunityFeed mount), so stash the celebration for the feed
        // to pick up on arrival — otherwise it would be lost across navigation.
        try {
          sessionStorage.setItem(`cwelcome_${token}`, res.data.member?.name || "");
        } catch {
          /* sessionStorage unavailable — fall back to the in-page shower below */
        }
        setWelcome({ show: true, name: res.data.member?.name || "" });
      } else if (res.data?.closed_group) {
        // Closed group + email not on the allow-list: block registration —
        // close the form and show the reason in a popup.
        setModal(null);
        setForm(EMPTY_FORM);
        setPendingGroup(null);
        setClosedError(res.data?.msg || "");
      } else {
        setMsg({ type: "error", text: res.data?.msg || "Could not join." });
      }
    } catch (err) {
      if (err.response?.data?.closed_group) {
        setModal(null);
        setForm(EMPTY_FORM);
        setPendingGroup(null);
        setClosedError(err.response.data.msg || "");
      } else {
        setMsg({ type: "error", text: err.response?.data?.msg || "Something went wrong. Please try again." });
      }
    } finally {
      setBusy(false);
    }
  };

  const submitSignin = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMsg({ type: "", text: "" });
    try {
      const res = await axios.post(`${config.COMMUNITY_API_URL}/community-login`, {
        community_token: token,
        email: form.email,
        password: form.password,
      });
      if (res.data?.error === 0) {
        saveMember(res.data.member);
        setModal(null);
        setForm(EMPTY_FORM);
        toast.success("Successfully logged in!");
      } else {
        setMsg({ type: "error", text: res.data?.msg || "Invalid email or password." });
      }
    } catch (err) {
      setMsg({ type: "error", text: err.response?.data?.msg || "Something went wrong. Please try again." });
    } finally {
      setBusy(false);
    }
  };

  // 3-step OTP reset: send code → verify code → set new password.
  const submitForgot = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMsg({ type: "", text: "" });
    try {
      if (forgotStep === 1) {
        const res = await axios.post(`${config.COMMUNITY_API_URL}/community-send-otp`, {
          community_token: token,
          email: form.email,
        });
        if (res.data?.success) {
          setResetToken(res.data.token || "");
          // Local dev convenience: the API echoes the OTP back so we can prefill it.
          if (res.data.otp) setForm((f) => ({ ...f, otp: res.data.otp }));
          setForgotStep(2);
        } else {
          setMsg({ type: "error", text: res.data?.message || "Could not send the code." });
        }
      } else if (forgotStep === 2) {
        const res = await axios.post(`${config.COMMUNITY_API_URL}/community-verify-otp`, {
          community_token: token,
          email: form.email,
          otp: form.otp,
          token: resetToken,
        });
        if (res.data?.success) {
          setForgotStep(3);
        } else {
          setMsg({ type: "error", text: res.data?.message || "Invalid code." });
        }
      } else {
        const res = await axios.post(`${config.COMMUNITY_API_URL}/community-reset-password`, {
          community_token: token,
          email: form.email,
          otp: form.otp,
          token: resetToken,
          password: form.password,
          password_confirmation: form.password_confirmation,
        });
        if (res.data?.success) {
          setForgotStep(1);
          setMsg({ type: "success", text: res.data?.message || "Your password has been reset." });
        } else {
          setMsg({ type: "error", text: res.data?.message || "Could not reset your password." });
        }
      }
    } catch (err) {
      setMsg({
        type: "error",
        text: err.response?.data?.message || err.response?.data?.msg || "Something went wrong. Please try again.",
      });
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <PageLoader label="" primary={primary} />
    );
  }
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f7f7f8] px-4">
        <p className="text-red-500 text-center">{error}</p>
      </div>
    );
  }

  // Group pages open inline on the same /:token page (no separate route): the
  // active section is driven by ?page=, so the header highlights it and the
  // URL is shareable. Guests can browse too — CommunityFeed renders read-only
  // and prompts Sign In on any write/restricted action.
  if (inGroupPage) {
    return (
      <CommunityFeed
        token={token}
        groupToken={gid}
        refCode={refCode}
        focusPostId={focusPost}
        // Frontend layout the owner picked — "studio" gives the in-group feed a
        // left sidebar nav to match the community-home Studio shell.
        template={community?.template || "classic"}
        // Mirror member changes (sign in / sign out done inside the feed) into
        // this page's state so the community view reflects them without a refresh.
        onMemberChange={(m) => (m ? saveMember(m) : signOut())}
        // Referral links hide the "Community" tab so the referred user stays
        // focused on the group they were invited to.
        navTabs={refCode ? NAV_TABS.filter((t) => t.key !== "groups") : NAV_TABS}
        page={page}
        onChangePage={(p) =>
          setSearchParams(refCode ? { gid, page: p, ref: refCode } : { gid, page: p })
        }
        policies={community?.policies}
        communityName={community?.name}
        // Group switcher in the in-group header: the member's joined groups +
        // jump-to-feed / discover handlers.
        myGroups={myGroups}
        onSwitchGroup={(gt) => setSearchParams(refCode ? { gid: gt, page: "feed", ref: refCode } : { gid: gt, page: "feed" })}
        onDiscoverGroups={() => setSearchParams({})}
        // Just leave the group page; the effect below reloads the groups once
        // member state has settled (avoids a stale reload racing logout).
        onNavigate={(v) => {
          setSearchParams({});
          setView(v);
        }}
        // Clicking a group owner (e.g. on the in-group About tab) leaves the
        // group page and opens that owner's "Owned by" group-list page.
        onInstructor={(instructorId, accent, dark) => {
          if (!instructorId) return;
          setInstructorAccent(accent || "");
          setInstructorDark(!!dark);
          setSearchParams({});
          setInstructorView(String(instructorId));
        }}
        onBack={() => {
          setSearchParams({});
        }}
      />
    );
  }

  // Instructor details page — shown via in-page state (no URL param).
  if (instructorView) {
    return (
      <>
        <InstructorDetails
          token={token}
          instructorId={instructorView}
          accent={instructorAccent}
          dark={instructorDark}
          member={member}
          onBack={() => setInstructorView(null)}
          onLogin={() => openModal("signin")}
          // VIEW on an owned group opens that group's landing page.
          onView={(g) => {
            setInstructorView(null);
            setSearchParams({ gid: g.group_token });
          }}
          // Group switcher in the header — jump to a joined group's feed or
          // return to the group collection.
          myGroups={myGroups}
          onSwitchGroup={(gt) => {
            setInstructorView(null);
            setSearchParams(
              refCode
                ? { gid: gt, page: "feed", ref: refCode }
                : { gid: gt, page: "feed" }
            );
          }}
          onDiscoverGroups={() => {
            setInstructorView(null);
            setSearchParams({});
          }}
        />

        {modal && (
          <AuthModal
            mode={modal}
            community={community}
            primary={primary}
            form={form}
            setForm={setForm}
            busy={busy}
            msg={msg}
            onClose={closeModal}
            onSwitch={openModal}
            onSubmitJoin={submitJoin}
            onSubmitSignin={submitSignin}
            forgotStep={forgotStep}
            onSubmitForgot={submitForgot}
          />
        )}
        <WelcomeFlowers show={welcome.show} name={welcome.name} onDone={() => setWelcome({ show: false, name: "" })} />
        <ClosedGroupErrorModal show={!!closedError} message={closedError} primary={primary} onClose={() => setClosedError("")} />
      </>
    );
  }

  // Group landing page opens at /:token?gid={group_token}
  if (gid) {
    return (
      <>
        <GroupLanding
          token={token}
          gid={gid}
          member={member}
          onBack={() => setSearchParams({})}
          // Header "Log In" (guests): open the Sign In popup, stay on the
          // landing page. After auth the pendingGroup effect lands them in the
          // group feed.
          onLogin={(g) => {
            setPendingGroup(g);
            setPendingGroupAction("login");
            openModal("signin");
          }}
          onJoin={(g) => {
            const pricing = g.pricing_type || "free";
            // Guests sign up / sign in first, then land back on this same page.
            if (!member) {
              setPendingGroup(g);
              setPendingGroupAction("join");
              openModal("join");
              return;
            }
            // Logged-in: paid (or expired free-trial) → purchase/subscription.
            if (pricing === "paid" || (pricing === "free_trial" && g.joined && g.trial_expired)) {
              window.open(PAID_CHECKOUT_URL, "_blank", "noopener,noreferrer");
              return;
            }
            if (g.joined) {
              setSearchParams({ gid: g.group_token, page: "feed" });
            } else {
              // Signed in but not a member yet — join, then enter the feed.
              doGroupJoin(g).then((data) => {
                if (data?.error === 0 && data.joined) {
                  setSearchParams({ gid: g.group_token, page: "feed" });
                }
              });
            }
          }}
          // Click "By <instructor>" -> instructor details page (in-page state,
          // no URL param).
          onInstructor={(instructorId, accent, dark) => instructorId && (setInstructorAccent(accent || ""), setInstructorDark(!!dark), setInstructorView(String(instructorId)))
          }
          // Header group switcher — jump between the member's joined groups.
          myGroups={myGroups}
          onSwitchGroup={(gt) =>
            setSearchParams(
              refCode
                ? { gid: gt, page: "feed", ref: refCode }
                : { gid: gt, page: "feed" }
            )
          }
          onDiscoverGroups={() => setSearchParams({})}
        />

        {/* Auth modal — opened on demand by Log In / Join (not auto) */}
        {modal && (
          <AuthModal
            mode={modal}
            community={community}
            primary={primary}
            form={form}
            setForm={setForm}
            busy={busy}
            msg={msg}
            onClose={closeModal}
            onSwitch={openModal}
            onSubmitJoin={submitJoin}
            onSubmitSignin={submitSignin}
            forgotStep={forgotStep}
            onSubmitForgot={submitForgot}
          />
        )}
        <WelcomeFlowers show={welcome.show} name={welcome.name} onDone={() => setWelcome({ show: false, name: "" })} />
        <ClosedGroupErrorModal show={!!closedError} message={closedError} primary={primary} onClose={() => setClosedError("")} />
      </>
    );
  }

  // Which frontend layout the owner picked (Templates popup). Defaults to the
  // current "classic" shell; "studio" is the sidebar/library layout.
  const template = community?.template || "classic";

  // Community logo (uploaded) with FlexiCommunity brand fallback — shared by
  // both shells.
  const logoEl =
    community?.logo && String(community.logo).trim() && !communityLogoFailed ? (
      <img
        src={community.logo}
        alt={community?.name}
        className="h-9 w-auto max-w-[150px] object-contain"
        onError={() => setCommunityLogoFailed(true)}
      />
    ) : (
      <img
        src={`${process.env.PUBLIC_URL || ""}/images/flexicommunity-logo.png`}
        alt="FlexiCommunity"
        className="h-9 w-auto max-w-[190px] object-contain"
      />
    );

  // Group switcher (same control as the in-group feed header) — lets a signed-in
  // member with joined groups jump straight into a group's feed or discover more.
  const groupSwitcherEl =
    member && myGroups.length > 0 ? (
      <GroupSwitcher
        groups={myGroups}
        currentToken={undefined}
        primary={primary}
        onSelect={(gt) =>
          setSearchParams(
            refCode
              ? { gid: gt, page: "feed", ref: refCode }
              : { gid: gt, page: "feed" }
          )
        }
        onDiscover={() => setSearchParams({})}
      />
    ) : null;

  // Signed-in member controls (notifications + menu); null for guests.
  const profileControls = member ? (
    <>
      <NotificationBell token={token} member={member} primary={primary} />
      <MemberMenu
        member={member}
        onChangePassword={() => setShowChangePassword(true)}
        primary={primary}
        onProfile={() => setShowProfile(true)}
        onAccountSettings={() => setShowAccountSettings(true)}
        onSignOut={signOut}
      />
    </>
  ) : null;

  // The active in-page section (groups library / members / leaderboard / …).
  // Identical content for both shells — only the surrounding chrome differs.
  const activeView =
    view === "members" ? (
      <MembersView token={token} primary={primary} member={member} />
    ) : view === "events" ? (
      <EventsView token={token} primary={primary} />
    ) : view === "leaderboard" ? (
      <LeaderboardView token={token} primary={primary} member={member} />
    ) : view === "rewards" ? (
      <RewardsView token={token} primary={primary} member={member} />
    ) : (
      <>
        {notice && (
          <div className="mb-4 text-sm rounded-xl px-3.5 py-2.5 bg-amber-50 text-amber-700 border border-amber-100">
            {notice}
          </div>
        )}

        {groups.length > 0 && (
          <div className="max-w-3xl mx-auto mb-8 flex items-center gap-3">
            <div className="group relative flex-1">
              <span
                className="absolute inset-y-0 left-5 flex items-center text-gray-400 pointer-events-none"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" />
                </svg>
              </span>
              <input
                type="text"
                value={groupQuery}
                onChange={(e) => setGroupQuery(e.target.value)}
                placeholder="Search for anything"
                onFocus={(e) => {
                  e.target.style.borderColor = primary;
                  e.target.style.boxShadow = `0 0 0 3px ${primary}1f`;
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "";
                  e.target.style.boxShadow = "";
                }}
                className="w-full h-[54px] rounded-2xl border border-gray-200/80 bg-white shadow-[0_4px_20px_-6px_rgba(0,0,0,0.10)] pl-14 pr-5 text-[15px] text-gray-700 placeholder-gray-400 outline-none focus:outline-none transition-shadow"
              />
            </div>
            <GroupFilter
              primary={primary}
              open={filterOpen}
              setOpen={setFilterOpen}
              typeFilter={typeFilter}
              setTypeFilter={setTypeFilter}
              priceFilter={priceFilter}
              setPriceFilter={setPriceFilter}
              activeFilters={activeFilters}
            />
          </div>
        )}

        {groups.length === 0 ? (
          <div className="text-center text-gray-500 bg-white rounded-2xl border border-gray-200 shadow-sm py-16">
            No groups in this community yet.
          </div>
        ) : visibleGroups.length === 0 ? (
          <div className="text-center text-gray-500 bg-white rounded-2xl border border-gray-200 shadow-sm py-16">
            No groups match your search or filters.
          </div>
        ) : (
          <>
            <GroupSection
              title="Groups"
              count={visibleGroups.length}
              groups={pagedGroups}
              primary={primary}
              groupBusyId={groupBusyId}
              onCardClick={onGroupClick}
              onOpen={openFeed}
              onInstructor={(instructorId, accent, dark) => instructorId && (setInstructorAccent(accent || ""), setInstructorDark(!!dark), setInstructorView(String(instructorId)))
              }
            />
            <Pagination
              page={safeGroupPage}
              totalPages={totalGroupPages}
              primary={primary}
              onChange={(p) => {
                setGroupPage(p);
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
            />
          </>
        )}
      </>
    );

  // Floating overlays + footer shared by both shells (only one shell renders
  // per pass, so there's no double-mount).
  const overlays = (
    <>
      {modal && (
        <AuthModal
          mode={modal}
          community={community}
          primary={primary}
          form={form}
          setForm={setForm}
          busy={busy}
          msg={msg}
          onClose={closeModal}
          onSwitch={openModal}
          onSubmitJoin={submitJoin}
          onSubmitSignin={submitSignin}
          forgotStep={forgotStep}
          onSubmitForgot={submitForgot}
        />
      )}

      <WelcomeFlowers show={welcome.show} name={welcome.name} onDone={() => setWelcome({ show: false, name: "" })} />
      <ClosedGroupErrorModal show={!!closedError} message={closedError} primary={primary} onClose={() => setClosedError("")} />

      {joinGroup && (
        <GroupJoinModal
          group={joinGroup}
          primary={primary}
          busy={groupBusyId === joinGroup.group_id}
          message={joinMsg}
          onConfirm={confirmGroupJoin}
          onClose={() => {
            if (groupBusyId === joinGroup.group_id) return;
            setJoinGroup(null);
            setJoinMsg("");
          }}
        />
      )}

      {showProfile && member && (
        <ProfileModal
          token={token}
          member={member}
          groupToken={gid}
          primary={primary}
          banner={community?.banner}
          onClose={() => setShowProfile(false)}
          onUpdated={saveMember}
        />
      )}

      {showChangePassword && member && (
        <ChangePasswordModal
          token={token}
          member={member}
          primary={primary}
          banner={community?.banner}
          onClose={() => setShowChangePassword(false)}
        />
      )}

      {showAccountSettings && member && (
        <AccountSettingsModal
          token={token}
          member={member}
          primary={primary}
          banner={community?.banner}
          onClose={() => setShowAccountSettings(false)}
        />
      )}

      <PolicyFooter policies={community?.policies} communityName={community?.name} primary={primary} showBranding={community?.show_branding} />

      <BannerPopup token={token} placement="community_page" />
    </>
  );

  // ===================== STUDIO shell (sidebar + library) =====================
  if (template === "studio") {
    const navBtn = (item, mobile) => {
      const active = view === item.key;
      return (
        <button
          key={item.key}
          onClick={() => setView(item.key)}
          className={
            (mobile
              ? "shrink-0 inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[13px] font-semibold transition-colors "
              : "w-full inline-flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold transition-colors ") +
            (active ? "" : "text-gray-600 hover:bg-gray-100")
          }
          style={active ? { backgroundColor: `${primary}14`, color: primary } : undefined}
        >
          {item.icon}
          {item.label}
        </button>
      );
    };

    return (
      <div className="min-h-screen flex bg-[#fbfbfc]">
        {/* Sidebar (desktop) */}
        <aside className="hidden lg:flex w-64 shrink-0 flex-col border-r border-gray-100 bg-white sticky top-0 h-screen">
          <div className="flex h-16 items-center px-6 border-b border-gray-50">{logoEl}</div>
          <div className="flex-1 overflow-y-auto px-4 py-5">
            <div className="px-2">
              <h1 className="text-base font-bold leading-snug text-gray-900">{community?.name}</h1>
              {subtitle && <p className="mt-1 text-xs leading-relaxed text-gray-500 line-clamp-4">{subtitle}</p>}
              <div className="mt-3 flex flex-wrap gap-1.5">
                <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-1 text-[11px] font-semibold text-gray-600">
                  {METRIC_ICONS.members}
                  <b>{totalMembers}</b> members
                </span>
                <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-1 text-[11px] font-semibold text-gray-600">
                  {METRIC_ICONS.groups}
                  <b>{groups.length}</b> groups
                </span>
              </div>
            </div>
            <nav className="mt-6 space-y-1">{STUDIO_NAV.map((item) => navBtn(item, false))}</nav>
          </div>
          {!member && (
            <div className="border-t border-gray-50 px-4 py-4">
              <button
                onClick={() => openModal("join")}
                style={{ backgroundColor: primary }}
                className="w-full rounded-xl py-2.5 font-bold text-white shadow-lg transition hover:opacity-90"
              >
                Join Community
              </button>
            </div>
          )}
        </aside>

        {/* Main column */}
        <div className="flex min-w-0 flex-1 flex-col">
          <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-gray-100 bg-white/85 px-4 backdrop-blur-md sm:px-6 lg:px-8">
            <div className="lg:hidden">{logoEl}</div>
            <div className="flex-1" />
            {member ? (
              <div className="flex items-center gap-1.5">{profileControls}</div>
            ) : (
              <button
                onClick={() => openModal("join")}
                style={{ backgroundColor: primary }}
                className="rounded-full px-5 py-2 text-sm font-bold text-white shadow lg:hidden"
              >
                Join
              </button>
            )}
          </header>

          {/* Mobile section nav */}
          <div className="border-b border-gray-100 bg-white lg:hidden">
            <div className="flex gap-1.5 overflow-x-auto px-3 py-2">
              {STUDIO_NAV.map((item) => navBtn(item, true))}
            </div>
          </div>

          <main className="mx-auto w-full max-w-[1100px] flex-1 px-4 py-8 sm:px-6 lg:px-10">
            <h2 className="mb-6 text-2xl font-bold tracking-tight text-gray-900">
              {STUDIO_TITLES[view] || "Library"}
            </h2>
            {activeView}
          </main>
        </div>

        {overlays}
      </div>
    );
  }

  // ===================== CATALOG shell (search + rails) =====================
  if (template === "catalog") {
    const hasQuery = !!q || activeFilters > 0;
    const cardFor = (g) => (
      <GroupCard
        key={g.group_id}
        g={g}
        primary={primary}
        busy={groupBusyId === g.group_id}
        onClick={() => onGroupClick(g)}
        onOpen={() => openFeed(g)}
        onInstructor={(instructorId, accent, dark) => instructorId && (setInstructorAccent(accent || ""), setInstructorDark(!!dark), setInstructorView(String(instructorId)))}
      />
    );
    // Catalog organises groups into horizontal "rails" by category — only the
    // rails that actually have groups are shown. A search/filter collapses the
    // rails into one flat results grid.
    const catalogRails = [
      member && myGroups.length ? { title: "Your groups", items: myGroups } : null,
      { title: "Free to join", items: discoverGroups.filter((g) => (g.pricing_type || "free") === "free") },
      { title: "Premium", items: discoverGroups.filter((g) => g.pricing_type === "paid") },
      { title: "Free trial", items: discoverGroups.filter((g) => g.pricing_type === "free_trial") },
    ].filter((r) => r && r.items.length);

    const groupsCatalog = (
      <>
        {notice && (
          <div className="mb-4 text-sm rounded-xl px-3.5 py-2.5 bg-amber-50 text-amber-700 border border-amber-100">
            {notice}
          </div>
        )}
        {groups.length === 0 ? (
          <div className="text-center text-gray-500 bg-white rounded-2xl border border-gray-200 shadow-sm py-16">
            No groups in this community yet.
          </div>
        ) : hasQuery ? (
          visibleGroups.length === 0 ? (
            <div className="text-center text-gray-500 bg-white rounded-2xl border border-gray-200 shadow-sm py-16">
              No groups match your search or filters.
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {visibleGroups.map(cardFor)}
            </div>
          )
        ) : (
          <div className="space-y-10">
            {catalogRails.map((rail) => (
              <section key={rail.title}>
                <h2 className="mb-4 text-lg font-bold text-gray-900">
                  {rail.title} <span className="text-base text-gray-400">({rail.items.length})</span>
                </h2>
                <CardRail primary={primary}>
                  {rail.items.map((g) => (
                    <div key={g.group_id} className="w-72 shrink-0 snap-start">
                      {cardFor(g)}
                    </div>
                  ))}
                </CardRail>
              </section>
            ))}
          </div>
        )}
      </>
    );

    return (
      <div className="min-h-screen flex flex-col bg-[#fbfbfc]">
        <header className="sticky top-0 z-30 border-b border-gray-100 bg-white/90 backdrop-blur-md">
          <div className="mx-auto flex h-16 max-w-[1200px] items-center gap-3 px-4 sm:px-6">
            <div className="shrink-0 flex items-center gap-2">{logoEl}{groupSwitcherEl}</div>
            <div className="relative max-w-xl flex-1">
              <span className="pointer-events-none absolute inset-y-0 left-3.5 flex items-center text-gray-400">
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" />
                </svg>
              </span>
              <input
                type="text"
                value={groupQuery}
                onChange={(e) => setGroupQuery(e.target.value)}
                placeholder="Search groups"
                onFocus={(e) => {
                  e.target.style.borderColor = primary;
                  e.target.style.boxShadow = `0 0 0 3px ${primary}22`;
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "";
                  e.target.style.boxShadow = "";
                }}
                className="w-full rounded-full border border-gray-200 bg-gray-50 py-2.5 pl-11 pr-4 text-sm text-gray-700 placeholder-gray-400 outline-none transition-colors focus:bg-white"
              />
            </div>
            <div className="hidden sm:block">
              <GroupFilter
                primary={primary}
                open={filterOpen}
                setOpen={setFilterOpen}
                typeFilter={typeFilter}
                setTypeFilter={setTypeFilter}
                priceFilter={priceFilter}
                setPriceFilter={setPriceFilter}
                activeFilters={activeFilters}
              />
            </div>
            <div className="flex items-center gap-1.5">
              {member ? (
                profileControls
              ) : (
                <button
                  onClick={() => openModal("join")}
                  style={{ backgroundColor: primary }}
                  className="rounded-full px-5 py-2 text-sm font-bold text-white shadow transition hover:opacity-90"
                >
                  Join
                </button>
              )}
            </div>
          </div>
        </header>

        <main className="mx-auto w-full max-w-[1200px] flex-1 px-4 py-8 sm:px-6">
          {view === "groups" ? (
            <>
              <h1 className="text-2xl font-bold tracking-tight text-gray-900">{community?.name}</h1>
              {subtitle && <p className="mb-6 mt-1 max-w-2xl text-sm text-gray-500 line-clamp-2">{subtitle}</p>}
              {!subtitle && <div className="mb-6" />}
              {groupsCatalog}
            </>
          ) : (
            activeView
          )}
        </main>

        {overlays}
      </div>
    );
  }

  // ===================== CAMPUS shell (learning dashboard) =====================
  if (template === "campus") {
    const firstName = member?.name ? String(member.name).trim().split(/\s+/)[0] : "";
    const joinedCount = myGroups.length;
    const totalCount = groups.length;
    const pct = totalCount ? Math.round((joinedCount / totalCount) * 100) : 0;
    const hasQuery = !!q || activeFilters > 0;
    const cardFor = (g) => (
      <GroupCard
        key={g.group_id}
        g={g}
        primary={primary}
        busy={groupBusyId === g.group_id}
        onClick={() => onGroupClick(g)}
        onOpen={() => openFeed(g)}
        onInstructor={(instructorId, accent, dark) => instructorId && (setInstructorAccent(accent || ""), setInstructorDark(!!dark), setInstructorView(String(instructorId)))}
      />
    );
    const gridCls = "grid grid-cols-1 sm:grid-cols-2 gap-6";
    const statRow = (label, value) => (
      <div className="flex items-center justify-between py-2 text-sm">
        <span className="text-gray-500">{label}</span>
        <span className="font-bold text-gray-900">{value}</span>
      </div>
    );

    return (
      <div className="min-h-screen flex flex-col bg-gray-50">
        <header className="sticky top-0 z-30 border-b border-gray-100 bg-white">
          <div className="mx-auto flex h-16 max-w-[1180px] items-center gap-3 px-4 sm:px-6">
            <div className="shrink-0 flex items-center gap-2">{logoEl}{groupSwitcherEl}</div>
            <div className="flex-1" />
            {member ? (
              <div className="flex items-center gap-1.5">{profileControls}</div>
            ) : (
              <button
                onClick={() => openModal("join")}
                style={{ backgroundColor: primary }}
                className="rounded-full px-5 py-2 text-sm font-bold text-white shadow transition hover:opacity-90"
              >
                Join Community
              </button>
            )}
          </div>
        </header>

        <main className="mx-auto w-full max-w-[1180px] flex-1 px-4 py-8 sm:px-6">
          {view === "groups" ? (
            <>
              {/* Welcome + progress */}
              <section className="mb-8 rounded-2xl border border-gray-200 bg-white p-6">
                <h1 className="text-2xl font-bold tracking-tight text-gray-900">
                  {firstName ? `Welcome back, ${firstName}` : "Welcome"}
                </h1>
                <p className="mt-1 text-sm text-gray-500">{community?.name}</p>
                {member && totalCount > 0 && (
                  <div className="mt-5 max-w-md">
                    <div className="mb-1.5 flex justify-between text-xs font-semibold text-gray-500">
                      <span>Groups joined</span>
                      <span>
                        {joinedCount} of {totalCount}
                      </span>
                    </div>
                    <div className="h-2 w-full rounded-full bg-gray-100">
                      <div
                        className="h-2 rounded-full transition-all"
                        style={{ width: `${pct}%`, backgroundColor: primary }}
                      />
                    </div>
                  </div>
                )}
              </section>

              <div className="grid gap-8 lg:grid-cols-[1fr_300px]">
                <div className="min-w-0">
                  {notice && (
                    <div className="mb-4 text-sm rounded-xl px-3.5 py-2.5 bg-amber-50 text-amber-700 border border-amber-100">
                      {notice}
                    </div>
                  )}

                  {groups.length > 0 && (
                    <div className="relative mb-6">
                      <span className="pointer-events-none absolute inset-y-0 left-4 flex items-center text-gray-400">
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" />
                        </svg>
                      </span>
                      <input
                        type="text"
                        value={groupQuery}
                        onChange={(e) => setGroupQuery(e.target.value)}
                        placeholder="Search groups"
                        onFocus={(e) => {
                          e.target.style.borderColor = primary;
                          e.target.style.boxShadow = `0 0 0 3px ${primary}22`;
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = "";
                          e.target.style.boxShadow = "";
                        }}
                        className="w-full rounded-xl border border-gray-200 bg-white py-3 pl-12 pr-4 text-sm text-gray-700 placeholder-gray-400 outline-none transition-colors"
                      />
                    </div>
                  )}

                  {groups.length === 0 ? (
                    <div className="rounded-2xl border border-gray-200 bg-white py-16 text-center text-gray-500">
                      No groups in this community yet.
                    </div>
                  ) : hasQuery ? (
                    visibleGroups.length === 0 ? (
                      <div className="rounded-2xl border border-gray-200 bg-white py-16 text-center text-gray-500">
                        No groups match your search.
                      </div>
                    ) : (
                      <div className={gridCls}>{visibleGroups.map(cardFor)}</div>
                    )
                  ) : (
                    <div className="space-y-8">
                      {member && myGroups.length > 0 && (
                        <section>
                          <h2 className="mb-4 text-lg font-bold text-gray-900">Continue</h2>
                          <div className={gridCls}>{myGroups.map(cardFor)}</div>
                        </section>
                      )}
                      {discoverGroups.length > 0 && (
                        <section>
                          <h2 className="mb-4 text-lg font-bold text-gray-900">
                            {member && myGroups.length > 0 ? "Explore more" : "Groups"}
                          </h2>
                          <div className={gridCls}>{discoverGroups.map(cardFor)}</div>
                        </section>
                      )}
                    </div>
                  )}
                </div>

                {/* Overview panel */}
                <aside className="space-y-4">
                  <div className="rounded-2xl border border-gray-200 bg-white p-5">
                    <h3 className="mb-2 text-sm font-bold text-gray-900">Overview</h3>
                    <div className="divide-y divide-gray-100">
                      {statRow("Groups joined", joinedCount)}
                      {statRow("Groups available", totalCount)}
                      {statRow("Members", totalMembers)}
                    </div>
                  </div>
                  {subtitle && (
                    <div className="rounded-2xl border border-gray-200 bg-white p-5">
                      <h3 className="mb-2 text-sm font-bold text-gray-900">About</h3>
                      <p className="text-sm leading-relaxed text-gray-500 line-clamp-6">{subtitle}</p>
                    </div>
                  )}
                </aside>
              </div>
            </>
          ) : (
            activeView
          )}
        </main>

        {overlays}
      </div>
    );
  }

  // ===================== CREATOR shell (mobile-first feed) =====================
  if (template === "creator") {
    const cardFor = (g) => (
      <GroupCard
        key={g.group_id}
        g={g}
        primary={primary}
        busy={groupBusyId === g.group_id}
        onClick={() => onGroupClick(g)}
        onOpen={() => openFeed(g)}
        onInstructor={(instructorId, accent, dark) => instructorId && (setInstructorAccent(accent || ""), setInstructorDark(!!dark), setInstructorView(String(instructorId)))}
      />
    );
    const avatarInitial = (community?.name || "?").charAt(0).toUpperCase();
    const bottomNav = [
      { key: "groups", label: "Home", icon: TAB_ICON.groups },
      { key: "members", label: "Members", icon: TAB_ICON.members },
      { key: "leaderboard", label: "Ranks", icon: TAB_ICON.leaderboard },
      { key: "rewards", label: "Rewards", icon: TAB_ICON.rewards },
    ];

    return (
      <div className="min-h-screen flex flex-col bg-gray-50 pb-20 lg:pb-0">
        {/* App bar */}
        <header className="sticky top-0 z-30 border-b border-gray-100 bg-white/90 backdrop-blur-md">
          <div className="mx-auto flex h-14 max-w-[640px] items-center gap-3 px-4">
            <div className="shrink-0 flex items-center gap-2">{logoEl}{groupSwitcherEl}</div>
            <div className="flex-1" />
            {member ? (
              <div className="flex items-center gap-1.5">{profileControls}</div>
            ) : (
              <button
                onClick={() => openModal("join")}
                style={{ backgroundColor: primary }}
                className="rounded-full px-4 py-1.5 text-sm font-bold text-white shadow"
              >
                Join
              </button>
            )}
          </div>
        </header>

        {/* Creator hero */}
        <div className="bg-white">
          <div className="relative h-28 w-full overflow-hidden sm:h-36">
            <img
              src={community?.banner || DEFAULT_BANNER}
              alt={community?.name}
              className="h-full w-full object-cover"
              onError={(e) => (e.currentTarget.src = DEFAULT_BANNER)}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
          </div>
          <div className="mx-auto max-w-[640px] px-4">
            <div className="-mt-10 flex items-end gap-3">
              {community?.logo && String(community.logo).trim() && !communityLogoFailed ? (
                <img
                  src={community.logo}
                  alt={community?.name}
                  className="h-20 w-20 rounded-full bg-white object-cover ring-4 ring-white"
                  onError={() => setCommunityLogoFailed(true)}
                />
              ) : (
                <div
                  className="flex h-20 w-20 items-center justify-center rounded-full text-2xl font-bold text-white ring-4 ring-white"
                  style={{ backgroundColor: primary }}
                >
                  {avatarInitial}
                </div>
              )}
              <div className="mb-1 flex flex-wrap gap-3 pb-1 text-sm text-gray-600">
                <span>
                  <b className="text-gray-900">{totalMembers}</b> members
                </span>
                <span>
                  <b className="text-gray-900">{groups.length}</b> groups
                </span>
              </div>
            </div>
            <h1 className="mt-3 text-xl font-extrabold tracking-tight text-gray-900">
              {community?.name}
            </h1>
            {subtitle && <p className="mt-1 text-sm leading-relaxed text-gray-500">{subtitle}</p>}
            {!member && (
              <button
                onClick={() => openModal("join")}
                style={{ backgroundColor: primary }}
                className="mt-4 w-full rounded-full py-2.5 font-bold text-white shadow-lg transition hover:opacity-90"
              >
                Join Community
              </button>
            )}
          </div>

          {/* Story rail — groups as tappable circles */}
          {groups.length > 0 && (
            <div className="mx-auto mt-5 max-w-[640px] border-t border-gray-100 px-4 py-4">
              <div className="flex gap-4 overflow-x-auto pb-1">
                {orderedGroups.map((g) => {
                  const real = !isEmptyBanner(g.banner);
                  return (
                    <button
                      key={g.group_id}
                      onClick={() => (g.joined ? openFeed(g) : onGroupClick(g))}
                      className="flex w-16 shrink-0 flex-col items-center gap-1.5"
                    >
                      <span className="rounded-full p-[2.5px]" style={{ backgroundColor: primary }}>
                        <span className="block h-14 w-14 overflow-hidden rounded-full bg-white ring-2 ring-white">
                          {real ? (
                            <img src={groupBanner(g.banner)} alt={g.name} className="h-full w-full object-cover" />
                          ) : (
                            <span
                              className="flex h-full w-full items-center justify-center text-lg font-bold text-white"
                              style={{ backgroundColor: primary }}
                            >
                              {(g.name || "?").charAt(0).toUpperCase()}
                            </span>
                          )}
                        </span>
                      </span>
                      <span className="w-full truncate text-center text-[11px] text-gray-600">{g.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Feed */}
        <main className="mx-auto w-full max-w-[600px] flex-1 px-4 py-6">
          {view === "groups" ? (
            <>
              {notice && (
                <div className="mb-4 text-sm rounded-xl px-3.5 py-2.5 bg-amber-50 text-amber-700 border border-amber-100">
                  {notice}
                </div>
              )}
              {groups.length === 0 ? (
                <div className="rounded-2xl border border-gray-200 bg-white py-16 text-center text-gray-500">
                  No groups in this community yet.
                </div>
              ) : visibleGroups.length === 0 ? (
                <div className="rounded-2xl border border-gray-200 bg-white py-16 text-center text-gray-500">
                  No groups match your search.
                </div>
              ) : (
                <div className="space-y-5">{visibleGroups.map(cardFor)}</div>
              )}
            </>
          ) : (
            activeView
          )}
        </main>

        {/* App-like bottom nav (mobile) */}
        <nav className="fixed inset-x-0 bottom-0 z-30 flex justify-around border-t border-gray-100 bg-white py-1.5 lg:hidden">
          {bottomNav.map((item) => {
            const active = view === item.key;
            return (
              <button
                key={item.key}
                onClick={() => setView(item.key)}
                className="flex flex-col items-center gap-0.5 px-3 py-1 text-[10px] font-semibold"
                style={{ color: active ? primary : "#9ca3af" }}
              >
                {item.icon}
                {item.label}
              </button>
            );
          })}
        </nav>

        {overlays}
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-[#fbfbfc] to-[#f3f4f6]">
      {/* ---------- Header bar: logo + nav menu + profile ---------- */}
      <header className="bg-white/85 backdrop-blur-md border-b border-gray-100 shadow-[0_1px_3px_rgba(0,0,0,0.04)] sticky top-0 z-30">
        <div className="px-4 sm:px-6 h-16 flex items-center gap-2 sm:gap-4">
          {/* Logo (top-left corner) — the community's own uploaded logo when
              set, otherwise fall back to the FlexiCommunity platform brand. */}
          <div className="shrink-0 flex items-center gap-2">{logoEl}{groupSwitcherEl}</div>

          {/* Nav menu — the community home shows only the groups list, so no
              header tabs here; Feed / Members / Leaderboard / Rewards / About
              are per-group and live inside each group (after access). This
              empty nav keeps the logo left-aligned and the profile menu right. */}
          <nav className="flex-1 min-w-0" />

          {/* Profile (only when signed in) */}
          <div className="shrink-0 flex items-center gap-1.5">{profileControls}</div>
        </div>
      </header>

      {/* ---------- Banner with overlaid community details ---------- */}
      <div className="w-full max-w-[1080px] mx-auto px-4 pt-6">
        {/* The banner grows with its content (min-h, not fixed h) so the desktop
            "Read more" can expand the description inline without scrolling or
            clipping the title. The image is absolute so the content drives the
            height; `flex` + `mt-auto` keeps the details pinned to the bottom. */}
        <div className="relative w-full min-h-[13rem] sm:min-h-[16rem] md:min-h-[18rem] overflow-hidden rounded-3xl ring-1 ring-black/5 shadow-xl bg-gray-900 flex">
          <img
            src={community?.banner || DEFAULT_BANNER}
            alt={community?.name}
            className="absolute inset-0 w-full h-full object-cover"
            onError={(e) => (e.currentTarget.src = DEFAULT_BANNER)}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-black/10" />

          {/* Name + description + stats (+ Join for guests) pinned to the bottom */}
          <div className="relative mt-auto w-full p-5 sm:p-7">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
              <div className="min-w-0">
                <h1 className="text-2xl font-bold tracking-tight text-white drop-shadow sm:text-3xl">
                  {community?.name}
                </h1>
                {subtitle && (
                  <>
                    {/* Mobile: truncate + "Read more" opens the bottom sheet (no
                        inline scroll inside the short banner). */}
                    <div className="md:hidden">
                      <p className="mt-1 max-w-2xl text-sm text-white/85 line-clamp-2 drop-shadow-sm">{subtitle}</p>
                      <button
                        type="button"
                        onClick={() => setShowBannerDesc(true)}
                        className="mt-1 text-xs font-semibold text-white underline underline-offset-2 drop-shadow-sm transition hover:text-white/80"
                      >
                        Read more
                      </button>
                    </div>
                    {/* Desktop: proper inline Read more — expands fully, no scroll
                        (the banner grows to fit). */}
                    <div className="hidden md:block">
                      <ExpandableText
                        text={subtitle}
                        collapsedRem={2.6}
                        expandedMax="2000px"
                        textClassName="mt-1 max-w-2xl text-sm text-white/85 drop-shadow-sm"
                        buttonClassName="mt-1 text-xs font-semibold text-white underline underline-offset-2 drop-shadow-sm transition hover:text-white/80"
                      />
                    </div>
                  </>
                )}
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-white/20 px-3 py-1 text-[13px] font-semibold text-white ring-1 ring-white/25 backdrop-blur-sm">
                    {METRIC_ICONS.members}
                    <b className="font-bold">{totalMembers}</b> members
                  </span>
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-white/20 px-3 py-1 text-[13px] font-semibold text-white ring-1 ring-white/25 backdrop-blur-sm">
                    {METRIC_ICONS.groups}
                    <b className="font-bold">{groups.length}</b> groups
                  </span>
                </div>
              </div>

              {!member && (
                <button
                  onClick={() => openModal("join")}
                  style={{ backgroundColor: primary }}
                  className="shrink-0 w-full rounded-full px-7 py-2.5 font-bold text-white shadow-lg transition hover:opacity-90 sm:w-auto"
                >
                  Join Community
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ---------- Active view ---------- */}
      <div className="w-full max-w-[1080px] mx-auto px-4 py-8">{activeView}</div>

      {/* Mobile: full community description in a bottom sheet. */}
      <MobileSheet open={showBannerDesc} onClose={() => setShowBannerDesc(false)}>
        <div className="p-5">
          <h3 className="font-bold text-white text-lg leading-snug">{community?.name}</h3>
          <p className="mt-3 text-sm leading-relaxed text-gray-200 whitespace-pre-line">{subtitle}</p>
        </div>
      </MobileSheet>

      {overlays}
    </div>
  );
}


/* ============================ Discover filter ============================ */

// A single radio-style row inside a filter column. Clicking the active option
// again clears it back to "All", so there's no separate "All" row to manage.
// Premium selectable chip used inside the filter groups.
function FilterChip({ label, selected, onSelect, primary, icon }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[13px] font-semibold transition-all"
      style={
        selected
          ? { borderColor: primary, backgroundColor: `${primary}14`, color: primary, boxShadow: `0 1px 6px -2px ${primary}66` }
          : { borderColor: "#e5e7eb", backgroundColor: "#fff", color: "#4b5563" }
      }
    >
      {selected ? (
        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3">
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      ) : (
        icon
      )}
      {label}
    </button>
  );
}

// One labelled group of filter chips (icon + heading + chips).
function FilterGroup({ icon, title, primary, children }) {
  return (
    <div>
      <div className="mb-2.5 flex items-center gap-2">
        <span className="flex h-6 w-6 items-center justify-center rounded-lg" style={{ backgroundColor: `${primary}14`, color: primary }}>
          {icon}
        </span>
        <p className="text-[13px] font-bold text-gray-900">{title}</p>
      </div>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}

// Filter button + premium popover for the discover grid. Facets are grouped
// (Type, then Price) and chosen as chips; re-tapping a chip clears that facet.
function GroupFilter({ primary, open, setOpen, typeFilter, setTypeFilter, priceFilter, setPriceFilter, activeFilters }) {
  const pickType = (v) => setTypeFilter((cur) => (cur === v ? "all" : v));
  const pickPrice = (v) => setPriceFilter((cur) => (cur === v ? "all" : v));

  const globeIcon = (
    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9 9 0 100-18 9 9 0 000 18zM3.6 9h16.8M3.6 15h16.8M12 3a14 14 0 000 18M12 3a14 14 0 010 18" />
    </svg>
  );
  const lockIcon = (
    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
      <path strokeLinecap="round" strokeLinejoin="round" d="M7 11V7a5 5 0 0110 0v4M6 11h12a1 1 0 011 1v7a1 1 0 01-1 1H6a1 1 0 01-1-1v-7a1 1 0 011-1z" />
    </svg>
  );
  const tagIcon = (
    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
      <path strokeLinecap="round" strokeLinejoin="round" d="M7 7h.01M3 4v5.6a2 2 0 00.6 1.4l8 8a2 2 0 002.8 0l5.2-5.2a2 2 0 000-2.8l-8-8A2 2 0 0011.6 2H6a3 3 0 00-3 3z" />
    </svg>
  );
  const giftIcon = (
    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v13M4 12v7a1 1 0 001 1h14a1 1 0 001-1v-7M3 8h18v4H3V8zm9 0S10 2 7 4s5 4 5 4zm0 0s2-6 5-4-5 4-5 4z" />
    </svg>
  );
  const dollarIcon = (
    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 2v20M16.5 7.5A4 4 0 0012.5 5h-1a3.5 3.5 0 000 7h1a3.5 3.5 0 010 7h-1a4 4 0 01-4-2.5" />
    </svg>
  );
  const clockIcon = (
    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 2m6-2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );

  return (
    <div className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="relative flex h-[54px] items-center gap-2 rounded-2xl border border-gray-200/80 bg-white px-5 text-sm font-medium text-gray-700 shadow-[0_4px_20px_-6px_rgba(0,0,0,0.10)] transition-colors hover:bg-gray-50"
      >
        <svg className="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 4h18M6 12h12M10 20h4" />
        </svg>
        <span className="hidden sm:inline">Filter</span>
        {activeFilters > 0 && (
          <span
            className="flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[11px] font-semibold text-white"
            style={{ background: primary }}
          >
            {activeFilters}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-20" onClick={() => setOpen(false)} />
          <div className="absolute right-0 z-30 mt-2 w-[300px] max-w-[calc(100vw-1.5rem)] overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-2xl ring-1 ring-gray-900/5">
            {/* header */}
            <div className="flex items-center justify-between border-b border-gray-100 bg-gradient-to-b from-gray-50/80 to-white px-4 py-3">
              <div className="flex items-center gap-2">
                <svg className="h-4 w-4" style={{ color: primary }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 4h18M6 12h12M10 20h4" />
                </svg>
                <p className="text-sm font-bold text-gray-900">Filters</p>
              </div>
              {activeFilters > 0 && (
                <span className="rounded-full px-2 py-0.5 text-[11px] font-bold" style={{ backgroundColor: `${primary}14`, color: primary }}>
                  {activeFilters} active
                </span>
              )}
            </div>

            {/* groups */}
            <div className="space-y-4 p-4">
              <FilterGroup icon={globeIcon} title="Type" primary={primary}>
                <FilterChip label="Public" icon={globeIcon} selected={typeFilter === "public"} onSelect={() => pickType("public")} primary={primary} />
                <FilterChip label="Private" icon={lockIcon} selected={typeFilter === "private"} onSelect={() => pickType("private")} primary={primary} />
              </FilterGroup>

              <div className="h-px bg-gray-100" />

              <FilterGroup icon={tagIcon} title="Price" primary={primary}>
                <FilterChip label="Free" icon={giftIcon} selected={priceFilter === "free"} onSelect={() => pickPrice("free")} primary={primary} />
                <FilterChip label="Paid" icon={dollarIcon} selected={priceFilter === "paid"} onSelect={() => pickPrice("paid")} primary={primary} />
                <FilterChip label="Free trial" icon={clockIcon} selected={priceFilter === "free_trial"} onSelect={() => pickPrice("free_trial")} primary={primary} />
              </FilterGroup>
            </div>

            {/* footer */}
            <div className="flex items-center justify-between border-t border-gray-100 px-4 py-3">
              <button
                type="button"
                disabled={activeFilters === 0}
                onClick={() => {
                  setTypeFilter("all");
                  setPriceFilter("all");
                }}
                className="text-xs font-semibold text-gray-500 transition hover:text-gray-700 disabled:opacity-40"
              >
                Clear all
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded-lg px-4 py-1.5 text-xs font-bold text-white shadow-sm transition hover:opacity-90"
                style={{ backgroundColor: primary }}
              >
                Done
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/* ============================ Groups ============================ */

/** Compact numbered pagination for the group list. Hidden when there's
 *  only a single page. Shows Prev / page numbers (with … ellipses) / Next. */
function Pagination({ page, totalPages, onChange, primary }) {
  if (totalPages <= 1) return null;

  // Build a compact range: always show first & last, the current page and its
  // neighbours, and collapse the rest into "…" placeholders.
  const pages = [];
  for (let p = 1; p <= totalPages; p++) {
    if (p === 1 || p === totalPages || (p >= page - 1 && p <= page + 1)) {
      pages.push(p);
    } else if (pages[pages.length - 1] !== "…") {
      pages.push("…");
    }
  }

  const btnBase =
    "min-w-[38px] h-[38px] px-3 inline-flex items-center justify-center rounded-lg text-sm font-semibold transition select-none";

  return (
    <nav className="mt-8 flex items-center justify-center gap-1.5" aria-label="Pagination">
      <button
        type="button"
        onClick={() => page > 1 && onChange(page - 1)}
        disabled={page <= 1}
        className={`${btnBase} border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed`}
        aria-label="Previous page"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
        </svg>
      </button>

      {pages.map((p, i) =>
        p === "…" ? (
          <span key={`e${i}`} className="min-w-[38px] h-[38px] inline-flex items-center justify-center text-gray-400">
            …
          </span>
        ) : (
          <button
            key={p}
            type="button"
            onClick={() => onChange(p)}
            aria-current={p === page ? "page" : undefined}
            className={`${btnBase} border ${
              p === page
                ? "text-white border-transparent"
                : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
            }`}
            style={p === page ? { backgroundColor: primary } : undefined}
          >
            {p}
          </button>
        )
      )}

      <button
        type="button"
        onClick={() => page < totalPages && onChange(page + 1)}
        disabled={page >= totalPages}
        className={`${btnBase} border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed`}
        aria-label="Next page"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
        </svg>
      </button>
    </nav>
  );
}

function GroupSection({ title, groups, count, emptyText, primary, groupBusyId, onCardClick, onOpen, onInstructor, className = "" }) {
  return (
    <section className={className}>
      <h2 className="text-lg font-bold text-gray-900 mb-5">
        {title} <span className="text-gray-400 text-base">({count ?? groups.length})</span>
      </h2>
      {groups.length === 0 ? (
        <div className="text-center text-gray-500 bg-white rounded-2xl border border-gray-200 shadow-sm py-12">
          {emptyText || "No groups here."}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {groups.map((g) => (
            <GroupCard
              key={g.group_id}
              g={g}
              primary={primary}
              busy={groupBusyId === g.group_id}
              onClick={() => onCardClick(g)}
              onOpen={() => onOpen(g)}
              onInstructor={onInstructor}
            />
          ))}
        </div>
      )}
    </section>
  );
}

/**
 * Full group details — the shared body used by both the desktop hover popover
 * and the mobile info modal. Shows the banner, name, visibility + pricing,
 * member count, instructor and the complete (scrollable) description.
 *
 *  variant: "popover" (desktop) | "modal" (mobile). The modal gets a close
 *  button header; the popover is a plain card.
 */
function GroupDetailsBody({ g, primary, onInstructor, onClose, variant = "popover", dark = false }) {
  const accessLabel = g.access_type === 2 ? "Private" : "Public";

  // Light (popover) vs dark (mobile sheet) colour tokens.
  const c = dark
    ? { name: "text-white", strong: "text-white", muted: "text-gray-300", dot: "text-gray-600", desc: "text-gray-200", border: "border-white/15", instr: "text-gray-200 hover:text-white" }
    : { name: "text-gray-900", strong: "text-gray-900", muted: "text-gray-500", dot: "text-gray-300", desc: "text-gray-600", border: "border-gray-200", instr: "text-gray-600 hover:text-gray-900" };

  return (
    <div className="flex flex-col">
      {/* banner — only in the mobile modal; the desktop tooltip skips the image
          and shows the text details only. */}
      {variant === "modal" && (
        <div className="relative aspect-[16/9] overflow-hidden bg-gray-100 rounded-t-2xl">
          <img
            src={groupBanner(g.banner)}
            alt={g.name}
            className={`w-full h-full object-fill${isEmptyBanner(g.banner) ? " grayscale" : ""}`}
            onError={(e) => (e.currentTarget.src = GROUP_DEFAULT_BANNER)}
          />
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="absolute top-2 right-2 h-8 w-8 rounded-full bg-black/55 backdrop-blur flex items-center justify-center text-white hover:bg-black/70 transition"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      )}

      <div className="p-4">
        {/* logo chip + name */}
        <div className="flex items-center gap-2.5">
          <span
            className="h-9 w-9 shrink-0 rounded-lg flex items-center justify-center text-sm font-bold overflow-hidden border"
            style={{
              backgroundColor: dark ? `${primary}33` : `${primary}1a`,
              borderColor: `${primary}66`,
              color: dark ? "#fff" : primary,
            }}
          >
            {(g.name || "?").charAt(0).toUpperCase()}
          </span>
          <h3 className={`font-bold leading-snug ${c.name}`}>{g.name}</h3>
        </div>

        {/* full description — scrolls if long */}
        <p className={`mt-3 text-sm leading-relaxed whitespace-pre-line overflow-y-auto max-h-48 md:max-h-60 ${c.desc}`}>
          {g.description || "—"}
        </p>

        {/* footer — single top border line, like the group card footer:
            visibility • members • instructor (no pricing badge). */}
        <div className={`mt-3 flex flex-wrap items-center gap-x-2.5 gap-y-1 border-t ${c.border} pt-3 text-xs`}>
          <span className={`font-semibold flex items-center gap-1 ${c.strong}`}>
            {g.access_type === 2 ? (
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                <path strokeLinecap="round" strokeLinejoin="round" d="M16 11V7a4 4 0 00-8 0v4M6 11h12v8a2 2 0 01-2 2H8a2 2 0 01-2-2v-8z" />
              </svg>
            ) : (
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9 9 0 100-18 9 9 0 000 18zM3.6 9h16.8M3.6 15h16.8M12 3a14 14 0 000 18M12 3a14 14 0 010 18" />
              </svg>
            )}
            {accessLabel}
          </span>
          <span className={c.dot}>•</span>
          <span className={c.muted}>
            {formatCount(g.member_count)} {g.member_count === 1 ? "Member" : "Members"}
          </span>
          {(() => {
            const insList = Array.isArray(g.instructors) && g.instructors.length
              ? g.instructors
              : (g.instructor ? [g.instructor] : []);
            if (!insList.length) return null;
            return (
              <>
                <span className={c.dot}>•</span>
                {insList.map((ins) => (
                  <button
                    key={ins.instructor_id}
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onInstructor && onInstructor(ins.instructor_id);
                    }}
                    className={`flex items-center gap-1 transition ${c.instr}`}
                  >
                    <InstructorAvatar
                      name={ins.name}
                      image={ins.profile_image}
                      primary={primary}
                      extraClass="shrink-0"
                    />
                    <span className="font-semibold truncate">{ins.name}</span>
                  </button>
                ))}
              </>
            );
          })()}
        </div>
      </div>
    </div>
  );
}

// Darken a hex color by a percentage (0.28 = 28% darker). Returns the input
// unchanged if it isn't a 3/6-digit hex.
function darken(hex, pct = 0.28) {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec((hex || "").trim());
  if (!m) return hex;
  let h = m[1];
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const n = parseInt(h, 16);
  const r = Math.max(0, Math.round(((n >> 16) & 255) * (1 - pct)));
  const g = Math.max(0, Math.round(((n >> 8) & 255) * (1 - pct)));
  const b = Math.max(0, Math.round((n & 255) * (1 - pct)));
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}

// Horizontal card rail with prev/next arrows (Udemy/Netflix-style). Both arrows
// stay visible whenever the rail can scroll; the one at the current edge is
// dimmed + disabled rather than hidden, so they're always discoverable. On
// touch devices users swipe the native scroll, so arrows are hidden below md.
function CardRail({ children, primary }) {
  const ref = useRef(null);
  const [scrollable, setScrollable] = useState(false);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  const update = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    setScrollable(el.scrollWidth - el.clientWidth > 4);
    setAtStart(el.scrollLeft <= 4);
    setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    update();
    // Re-measure after layout settles (fonts/images) and on any resize.
    const raf = requestAnimationFrame(update);
    const ro = new ResizeObserver(update);
    ro.observe(el);
    el.addEventListener("scroll", update, { passive: true });
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      el.removeEventListener("scroll", update);
    };
  }, [update]);

  const nudge = (dir) => {
    const el = ref.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.max(el.clientWidth * 0.85, 300), behavior: "smooth" });
  };

  const arrowCls =
    "absolute top-1/2 z-30 hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full text-white ring-2 ring-white shadow-2xl transition duration-200 md:flex disabled:cursor-default enabled:hover:scale-110 enabled:hover:opacity-90";
  // Solid fill in a darker shade of the theme (button) color, with a white ring
  // + strong shadow so it stays clearly visible even when the theme is a light
  // tint. Dynamic color → inline style.
  const baseStyle = { backgroundColor: darken(primary || "#0274F7") };

  return (
    <div className="relative">
      {scrollable && (
        <button type="button" aria-label="Scroll left" disabled={atStart} onClick={() => nudge(-1)} style={baseStyle} className={`${arrowCls} left-1`}>
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.4">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      )}
      <div ref={ref} className="flex gap-5 overflow-x-auto pb-2 snap-x scroll-smooth">
        {children}
      </div>
      {scrollable && (
        <button type="button" aria-label="Scroll right" disabled={atEnd} onClick={() => nudge(1)} style={baseStyle} className={`${arrowCls} right-1`}>
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.4">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      )}
    </div>
  );
}

function GroupCard({ g, primary, busy, onClick, onOpen, onInstructor }) {
  const initial = (g.name || "?").charAt(0).toUpperCase();
  // Hover popover (desktop) + info modal (mobile) state. `popSide` flips the
  // popover to the left when the card sits too close to the right edge.
  const [hovered, setHovered] = useState(false);
  const [popShown, setPopShown] = useState(false);
  const [popSide, setPopSide] = useState("right");
  const [showDetails, setShowDetails] = useState(false);
  const wrapRef = useRef(null);
  const closeTimer = useRef(null);

  // Open the popover, choosing the side with room for it.
  const openPopover = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    const el = wrapRef.current;
    if (el) {
      const rect = el.getBoundingClientRect();
      const POP_W = 340; // keep in sync with the popover width below
      setPopSide(rect.right + 16 + POP_W > window.innerWidth ? "left" : "right");
    }
    setHovered(true);
  };
  // Small close delay so moving the cursor onto the popover keeps it open.
  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setHovered(false), 150);
  };
  // Drive the enter/leave transition (mount → next frame → fade+slide in).
  useEffect(() => {
    if (!hovered) {
      setPopShown(false);
      return;
    }
    const id = requestAnimationFrame(() => setPopShown(true));
    return () => cancelAnimationFrame(id);
  }, [hovered]);
  // Clean up any pending close timer on unmount.
  useEffect(() => () => closeTimer.current && clearTimeout(closeTimer.current), []);
  // Pricing is separate from visibility: free | free_trial | paid.
  const pricing = g.pricing_type || "free";
  const isPaid = pricing === "paid";
  const isTrial = pricing === "free_trial";
  const trialLapsed = isTrial && g.joined && g.trial_expired;
  // Bare amount (strip any "Free trial · " prefix) for the bottom-right price.
  const amountLabel = (g.price_label || "").replace(/^Free trial · /, "") || formatPrice(g.amt);
  const accessLabel = g.access_type === 2 ? "Private" : "Public";

  // Paid groups send the user to checkout to buy access.
  const buyNow = () => window.open(PAID_CHECKOUT_URL, "_blank", "noopener,noreferrer");

  // Whole card is the action target (the reference design has button-less cards):
  // expired trial -> checkout, joined -> open the feed, pending -> no-op,
  // otherwise (free / paid / active free-trial) -> open the group landing page
  // (paid groups buy from there via the landing's "Get access" button).
  const handleClick = () => {
    if (busy) return;
    if (trialLapsed) return buyNow();
    if (g.joined) return onOpen();
    if (g.pending) return;
    onClick();
  };

  return (
    <div className="relative" ref={wrapRef} onMouseEnter={openPopover} onMouseLeave={scheduleClose}>
    <div
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleClick();
        }
      }}
      className="group bg-white rounded-2xl ring-1 ring-gray-200/80 shadow-sm overflow-hidden transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_18px_40px_-12px_rgba(0,0,0,0.22)] flex flex-col cursor-pointer"
    >
      {/* banner — inset with padding + its own rounded corners (course-card style) */}
      <div className="px-3 pt-3">
        <div className="aspect-[16/9] bg-gray-100 overflow-hidden relative rounded-xl ring-1 ring-black/5">
        <img
          src={groupBanner(g.banner)}
          alt={g.name}
          className={`w-full h-full object-fill transition-transform duration-500 group-hover:scale-105${
            isEmptyBanner(g.banner) ? " grayscale" : ""
          }`}
          onError={(e) => (e.currentTarget.src = GROUP_DEFAULT_BANNER)}
        />
        {/* Top-right badge stack: "Joined" sits in the corner, with the
            pricing-type label below it (so the banner's own logo on the left
            stays uncovered). */}
        <div className="absolute top-2 right-2 flex flex-col items-end gap-1.5">
          {g.joined && (
            <span className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-gray-700 bg-white shadow-sm ring-1 ring-black/5 px-2.5 py-1 rounded-full">
              <svg className="w-4 h-4 text-green-500 shrink-0" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.71-9.71a1 1 0 00-1.42-1.41L9 10.17 7.71 8.88a1 1 0 00-1.42 1.41l2 2a1 1 0 001.42 0l4.29-4.29z" clipRule="evenodd" />
              </svg>
              Joined
            </span>
          )}
          {/* Pricing-type label. Paid / free-trial get a premium gold gradient
              pill with the price highlighted in a white capsule; free groups
              keep the community-theme pill. */}
          {/* Paid / free-trial groups show a pricing pill. The "Free" badge is
              intentionally hidden for free groups (commented out per request). */}
          {(isPaid || isTrial) && (
            <span
              className="flex items-center gap-1 rounded-full bg-red-600 px-2 py-0.5 text-[11px] font-bold text-white shadow-sm ring-1 ring-black/5"
            >
              {isPaid ? (
                <svg className="w-3 h-3 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 2v20M16.5 7.5A4 4 0 0012.5 5h-1a3.5 3.5 0 000 7h1a3.5 3.5 0 010 7h-1a4 4 0 01-4-2.5" />
                </svg>
              ) : (
                <svg className="w-3 h-3 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 2m6-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              )}
              <span>{isPaid ? "Paid" : "Trial"}</span>
              {amountLabel && <span className="font-extrabold">{amountLabel}</span>}
            </span>
          )}
          {/* Free badge hidden per request:
          <span className="flex items-center gap-1 rounded-full bg-green-600 px-2 py-0.5 text-[11px] font-bold text-white shadow-sm ring-1 ring-black/5">
            <svg className="w-3 h-3 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v13M4 12v7a1 1 0 001 1h14a1 1 0 001-1v-7M3 8h18v4H3V8zm9 0S10 2 7 4s5 4 5 4zm0 0s2-6 5-4-5 4-5 4z" />
            </svg>
            Free
          </span> */}
        </div>
        </div>
      </div>

      {/* body */}
      <div className="p-4 flex flex-col flex-1">
        {/* logo chip + title */}
        <div className="flex items-center gap-2.5">
          <span
            className="h-9 w-9 shrink-0 rounded-lg flex items-center justify-center text-sm font-bold overflow-hidden border"
            style={{ backgroundColor: `${primary}1a`, borderColor: `${primary}40`, color: primary }}
          >
            {initial}
          </span>
          <h3 className="font-bold text-gray-900 line-clamp-1">{g.name}</h3>
        </div>

        {/* description — clamp to 3 lines with an ellipsis (reserve the height
            so every card is the same height). The full description (+ all group
            details) is revealed in the hover popover on desktop and via the
            "Read more" bottom sheet on mobile. */}
        <p className="text-sm text-gray-500 mt-2 line-clamp-3 min-h-[3.75rem]">{g.description || "—"}</p>
        {/* Mobile only — desktop uses the hover popover instead. */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setShowDetails(true);
          }}
          className="md:hidden mt-1 self-start text-xs font-semibold underline underline-offset-2 transition hover:opacity-80"
          style={{ color: primary }}
        >
          Read more
        </button>

        {/* meta — pinned to the bottom so cards line up. Order:
            visibility (Public/Private/Paid) • member count • instructor. */}
        <div className="mt-auto pt-3 flex items-center gap-1.5 text-xs">
          {busy ? (
            <span className="text-gray-900 font-semibold flex items-center gap-1">
              <Spinner size={14} />
              Joining…
            </span>
          ) : g.pending ? (
            <span className="text-gray-900 font-semibold flex items-center gap-1">
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 2m6-2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Pending
            </span>
          ) : (
            <span className="text-gray-900 font-semibold flex items-center gap-1">
              {g.access_type === 2 ? (
                /* closed padlock = Private */
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16 11V7a4 4 0 00-8 0v4M6 11h12v8a2 2 0 01-2 2H8a2 2 0 01-2-2v-8z" />
                </svg>
              ) : (
                /* globe = Public */
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9 9 0 100-18 9 9 0 000 18zM3.6 9h16.8M3.6 15h16.8M12 3a14 14 0 000 18M12 3a14 14 0 010 18" />
                </svg>
              )}
              {accessLabel}
            </span>
          )}
          <span className="text-gray-300 shrink-0">•</span>
          <span className="text-gray-500 shrink-0">
            {formatCount(g.member_count)} {g.member_count === 1 ? "Member" : "Members"}
          </span>
          {/* Instructor "By <name>" link — last item; opens the instructor page
              (stopPropagation so it doesn't trigger the card's join/open).
              Name truncates on narrow cards. */}
          {(() => {
            const insList = Array.isArray(g.instructors) && g.instructors.length
              ? g.instructors
              : (g.instructor ? [g.instructor] : []);
            if (!insList.length) return null;
            return (
              <>
                <span className="text-gray-300 shrink-0">•</span>
                {insList.map((ins) => (
                  <button
                    key={ins.instructor_id}
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onInstructor && onInstructor(ins.instructor_id);
                    }}
                    title={`By ${ins.name}`}
                    className="min-w-0 flex items-center gap-1 text-gray-500 hover:text-gray-900 hover:underline transition"
                  >
                    <InstructorAvatar
                      name={ins.name}
                      image={ins.profile_image}
                      primary={primary}
                      extraClass="shrink-0"
                    />
                    <span className="truncate">{ins.name}</span>
                  </button>
                ))}
              </>
            );
          })()}
        </div>
      </div>
    </div>

      {/* Desktop hover popover — full details beside the card. Hidden on mobile
          (md:block); the info-button modal handles touch. Flips left when the
          card is near the right viewport edge. */}
      {hovered && (
        <div
          onMouseEnter={openPopover}
          onMouseLeave={scheduleClose}
          className={`hidden md:block absolute top-0 z-50 w-[340px] ${
            popSide === "right" ? "left-full ml-3" : "right-full mr-3"
          } transition-all duration-200 ease-out ${
            popShown
              ? "opacity-100 translate-x-0"
              : `opacity-0 ${popSide === "right" ? "-translate-x-2" : "translate-x-2"}`
          }`}
        >
          {/* Tooltip arrow — a rotated square poking out toward the card, on the
              edge that faces it (left when the popover is on the right, etc).
              Filled with the dark theme (button) color so the pointer reads
              clearly instead of looking white. */}
          <span
            className={`absolute top-6 h-3 w-3 rotate-45 ring-1 ring-black/10 ${
              popSide === "right" ? "-left-1.5" : "-right-1.5"
            }`}
            style={{ backgroundColor: darken(primary || "#0274F7") }}
          />
          <div
            className="relative rounded-2xl text-gray-900 shadow-[0_24px_60px_-15px_rgba(0,0,0,0.3)] ring-1 ring-black/5 overflow-hidden"
            style={{ backgroundColor: "#fff", backgroundImage: `linear-gradient(0deg, ${primary}1f, ${primary}1f)` }}
          >
            <GroupDetailsBody g={g} primary={primary} onInstructor={onInstructor} variant="popover" />
          </div>
        </div>
      )}

      {/* Mobile bottom sheet — same dark details as the desktop popover. */}
      <MobileSheet open={showDetails} onClose={() => setShowDetails(false)}>
        <GroupDetailsBody g={g} primary={primary} onInstructor={onInstructor} variant="sheet" dark />
      </MobileSheet>
    </div>
  );
}

/* Confirmation modal shown when a member taps a group they haven't joined.
   Free groups join + open instantly; private groups send an approval request. */
function GroupJoinModal({ group, primary, busy, message, onConfirm, onClose }) {
  const isPrivate = group.access_type === 2;
  const initial = (group.name || "?").charAt(0).toUpperCase();

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
      onClick={onClose}
    >
      <div
        className="bg-white w-full max-w-md rounded-2xl overflow-hidden shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* banner */}
        <div className="h-36 bg-gray-100 overflow-hidden relative">
          <img
            src={groupBanner(group.banner)}
            alt={group.name}
            className={`w-full h-full object-cover${isEmptyBanner(group.banner) ? " grayscale" : ""}`}
            onError={(e) => (e.currentTarget.src = GROUP_DEFAULT_BANNER)}
          />
          <span
            className={`absolute top-2 right-2 text-[11px] font-semibold px-2 py-0.5 rounded-full flex items-center gap-1 ${
              isPrivate ? "text-white bg-gray-900/80" : "bg-white/95 shadow-sm"
            }`}
            style={isPrivate ? undefined : { color: primary }}
          >
            {isPrivate ? "Private" : "Free"}
          </span>
          <button
            onClick={onClose}
            className="absolute top-2 left-2 w-7 h-7 rounded-full bg-black/40 text-white flex items-center justify-center hover:bg-black/60"
            aria-label="Close"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-5">
          <div className="flex items-center gap-3">
            <span
              className="h-11 w-11 shrink-0 rounded-xl flex items-center justify-center text-white font-bold overflow-hidden"
              style={{ backgroundColor: primary }}
            >
              {initial}
            </span>
            <div className="min-w-0">
              <h3 className="font-bold text-gray-900 line-clamp-1">{group.name}</h3>
              <p className="text-xs text-gray-500">
                {formatCount(group.member_count)} {group.member_count === 1 ? "Member" : "Members"} •{" "}
                {isPrivate ? "Private group" : "Free group"}
              </p>
            </div>
          </div>

          {group.description && (
            <p className="text-sm text-gray-500 mt-3 line-clamp-3">{group.description}</p>
          )}

          <div
            className="flex items-start gap-2 text-sm rounded-xl px-3.5 py-2.5 mt-3 font-medium"
            style={{ backgroundColor: `${primary}14`, color: primary, border: `1px solid ${primary}33` }}
          >
            <svg className="w-4 h-4 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
              {isPrivate ? (
                <path strokeLinecap="round" strokeLinejoin="round" d="M16 11V7a4 4 0 00-8 0v4M6 11h12v8a2 2 0 01-2 2H8a2 2 0 01-2-2v-8z" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              )}
            </svg>
            <span>
              {isPrivate
                ? "This is a private group. Your request will be sent to the admin for approval."
                : "This is a free group. Join now to get instant access to the feed."}
            </span>
          </div>

          {message ? (
            <div className="mt-4">
              <div
                className="flex items-start gap-2 text-sm rounded-xl px-3.5 py-2.5 font-medium"
                style={{ backgroundColor: `${primary}14`, color: primary, border: `1px solid ${primary}33` }}
              >
                <svg className="w-4 h-4 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 2m6-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {message}
              </div>
              <button
                onClick={onClose}
                style={{ backgroundColor: primary }}
                className="w-full text-white font-semibold py-3 rounded-xl mt-4 hover:opacity-90 transition"
              >
                Done
              </button>
            </div>
          ) : (
            <button
              onClick={onConfirm}
              disabled={busy}
              style={{ backgroundColor: primary }}
              className="w-full text-white font-semibold py-3 rounded-xl mt-5 hover:opacity-90 transition disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {busy && (
                <Spinner size={16} />
              )}
              {busy ? "Please wait…" : isPrivate ? "Request to join" : "Join group"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// Compact member-count formatting (e.g. 80100 -> "80.1k") to match the design.
function formatCount(n) {
  const num = Number(n) || 0;
  if (num >= 1000) {
    const k = num / 1000;
    return (k >= 100 ? Math.round(k) : k.toFixed(1).replace(/\.0$/, "")) + "k";
  }
  return String(num);
}

/* ============================ Small UI helpers ============================ */

/**
 * Text block that clamps to a few lines and reveals the rest with a smooth
 * height animation on "Read more". The toggle only appears when the text is
 * actually long enough to overflow the collapsed height, and overflow is
 * re-measured on resize so it behaves on every screen size.
 *
 *  - collapsedRem : collapsed height (in rem) — set to the clamped line count
 *  - textClassName: typography/colour classes applied to the text
 *  - buttonClassName / moreLabel / lessLabel : the toggle styling + copy
 *  - stop : when true, the toggle click doesn't bubble (cards are clickable)
 */
function ExpandableText({
  text,
  collapsedRem = 2.6,
  expandedMax = "600px",
  textClassName = "",
  buttonClassName = "",
  buttonStyle = undefined,
  moreLabel = "Read more",
  lessLabel = "Read less",
  stop = false,
  expandedScroll = false,
  expandedMaxClass = "",
}) {
  const [expanded, setExpanded] = useState(false);
  const [overflow, setOverflow] = useState(false);
  const ref = useRef(null);

  // Decide whether the toggle is needed by comparing the text's full content
  // height (scrollHeight — stable in every state since overflow is clipped)
  // against the collapsed pixel height. This does NOT depend on the current
  // animated height, so "Read more" reappears correctly after "Read less",
  // and re-measures on resize so it's correct on every screen size.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => {
      const rootPx = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
      setOverflow(el.scrollHeight > collapsedRem * rootPx + 2);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [text, collapsedRem]);

  return (
    <div>
      <div
        ref={ref}
        className={`${expanded && expandedScroll ? "overflow-y-auto" : "overflow-hidden"} transition-[max-height] duration-500 ease-in-out ${textClassName} ${expanded && expandedMaxClass ? expandedMaxClass : ""}`}
        // When expanded with a responsive max-height class, let the class win
        // (omit the inline cap); otherwise use the inline expanded/collapsed cap.
        style={{ maxHeight: expanded ? (expandedMaxClass ? undefined : expandedMax) : `${collapsedRem}rem` }}
      >
        {text}
      </div>
      {(overflow || expanded) && (
        <button
          type="button"
          onClick={(e) => {
            if (stop) e.stopPropagation();
            setExpanded((v) => !v);
          }}
          className={buttonClassName}
          style={buttonStyle}
        >
          {expanded ? lessLabel : moreLabel}
        </button>
      )}
    </div>
  );
}

/**
 * Mobile-only bottom sheet (md:hidden). Slides up from the bottom, dims +
 * locks the background so it can't scroll behind, and has a drag handle +
 * close ✕. Used for the banner description and the group-card details so they
 * share one consistent mobile presentation.
 */
function MobileSheet({ open, onClose, children }) {
  const [shown, setShown] = useState(false);

  // Mount → next frame → slide up (so the transition actually runs).
  useEffect(() => {
    if (!open) {
      setShown(false);
      return;
    }
    const id = requestAnimationFrame(() => setShown(true));
    return () => cancelAnimationFrame(id);
  }, [open]);

  // Freeze the page scroll while open; restore on close/unmount.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      className={`md:hidden fixed inset-0 z-[60] flex items-end transition-colors duration-200 ${
        shown ? "bg-black/50" : "bg-black/0"
      }`}
      onClick={onClose}
    >
      <div
        className={`relative w-full max-h-[85vh] overflow-y-auto rounded-t-2xl bg-gray-900 text-white ring-1 ring-white/10 shadow-2xl transition-transform duration-300 ease-out ${
          shown ? "translate-y-0" : "translate-y-full"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* drag handle + close ✕ */}
        <div className="sticky top-0 z-10 flex items-center justify-center bg-gray-900/95 backdrop-blur pt-3 pb-1">
          <span className="h-1.5 w-10 rounded-full bg-white/25" />
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="absolute right-3 top-2 h-8 w-8 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function stripHtml(html) {
  if (!html) return "";
  return html
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/\s+/g, " ")
    .trim();
}

const METRIC_ICONS = {
  members: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
      <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-1a4 4 0 00-3-3.87M9 20H4v-1a4 4 0 013-3.87m6-1.13a4 4 0 10-4-4 4 4 0 004 4zm6-2a3 3 0 10-2.5-4.5" />
    </svg>
  ),
  groups: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 5h6v6H4V5zm10 0h6v6h-6V5zM4 15h6v4H4v-4zm10 0h6v4h-6v-4z" />
    </svg>
  ),
};


export default GroupCollection;
